import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ShieldCheck, Stethoscope, Video, ArrowRight, HeartPulse, Clock, BadgeCheck } from "lucide-react";

const HERO_IMG =
  "https://images.pexels.com/photos/8376171/pexels-photo-8376171.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Landing() {
  return (
    <div className="min-h-screen hero-bg-pattern">
      {/* Top brand bar */}
      <header className="w-full px-6 lg:px-10 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="h-9 w-9 rounded-xl bg-primary text-primary-foreground flex items-center justify-center">
            <Stethoscope className="h-5 w-5" />
          </span>
          <span className="font-semibold text-lg tracking-tight">
            Healthink<span className="text-primary">+</span>
          </span>
        </div>
        <Link
          to="/staff/login"
          data-testid="topbar-staff-login-link"
          className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
        >
          Staff sign in →
        </Link>
      </header>

      {/* Hero */}
      <section className="px-6 md:px-12 lg:px-24 pt-8 lg:pt-12 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 flex flex-col items-start gap-7">
            <span
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-primary bg-accent px-3 py-1.5 rounded-full"
            >
              <HeartPulse className="h-3.5 w-3.5" /> Telemedicine, Reimagined
            </span>
            <h1
              data-testid="hero-title"
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-foreground"
            >
              Care that comes <span className="text-primary">to you</span>,
              <br className="hidden sm:block" />
              not the other way around.
            </h1>
            <p
              data-testid="hero-subtitle"
              className="text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed"
            >
              Book a video consultation with verified specialists, walk into a partner clinic, or
              manage your practice — all from one place. Trusted by patients and providers across
              India.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <Link to="/patient/login" className="w-full sm:w-auto">
                <Button
                  data-testid="cta-patient-btn"
                  size="lg"
                  className="rounded-full px-8 h-14 text-base w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground shadow-md hover:shadow-lg transition-all"
                >
                  I'm a Patient
                  <ArrowRight className="h-5 w-5 ml-1" />
                </Button>
              </Link>
              <Link to="/staff/login" className="w-full sm:w-auto">
                <Button
                  data-testid="cta-staff-btn"
                  variant="outline"
                  size="lg"
                  className="rounded-full px-8 h-14 text-base w-full sm:w-auto border-border hover:bg-secondary hover:text-secondary-foreground"
                >
                  Staff Login
                </Button>
              </Link>
            </div>

            <div className="flex flex-wrap items-center gap-6 pt-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-primary" /> ABDM-aligned
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-primary" /> 24×7 video care
              </div>
              <div className="flex items-center gap-2">
                <BadgeCheck className="h-4 w-4 text-primary" /> Verified doctors
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="relative h-[420px] sm:h-[500px] lg:h-[620px] w-full rounded-[2rem] overflow-hidden shadow-xl">
              <img
                src={HERO_IMG}
                alt="Patient on a video consultation"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-teal-900/40 to-transparent mix-blend-multiply" />

              {/* Floating stat card */}
              <div className="absolute left-5 bottom-5 right-5 sm:right-auto sm:max-w-[280px] bg-white/95 backdrop-blur rounded-2xl p-5 shadow-lg border border-border">
                <div className="flex items-center gap-3">
                  <span className="h-10 w-10 rounded-xl bg-accent text-accent-foreground flex items-center justify-center">
                    <Video className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="text-2xl font-semibold tracking-tight">12k+</div>
                    <div className="text-xs text-muted-foreground">
                      Consultations completed this month
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Three-up trust section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-20">
          {[
            {
              icon: Video,
              title: "Video consult",
              body: "Connect with a specialist in under 10 minutes from your phone or laptop.",
            },
            {
              icon: Stethoscope,
              title: "Walk-in OPD",
              body: "Book a slot at a partner clinic near you — no waiting, no calls.",
            },
            {
              icon: ShieldCheck,
              title: "Private & secure",
              body: "Your records stay encrypted and yours. Share with one tap when needed.",
            },
          ].map((f, i) => (
            <div
              key={i}
              data-testid={`feature-card-${i}`}
              className="bg-white border border-border rounded-2xl p-6 sm:p-7 shadow-sm hover:shadow-md transition-shadow duration-300"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-accent text-accent-foreground mb-4">
                <f.icon className="h-5 w-5" />
              </span>
              <h3 className="text-xl font-semibold tracking-tight mb-1.5">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-border py-6 px-6 lg:px-10 text-xs text-muted-foreground flex flex-col sm:flex-row items-center justify-between gap-2">
        <div>© {new Date().getFullYear()} Healthink Healthcare. All rights reserved.</div>
        <div>Made for India · ₹ INR</div>
      </footer>
    </div>
  );
}
